import type {
    BarChartLayoutConstantType,
    BarChartBarSide,
    BarChartLayout,
    BarChartLabel,
    BarChartBar
} from "./type";
import {
    getChartLabelIndexes,
    getChartLinearScale,
    getChartTextWidth,
    getChartPlotRect,
    getChartXTicks,
    getChartBand
} from "../chart/util";

export const BAR_CHART_LABEL_BASELINE_SHIFT = 0.35;

export const BAR_CHART_MIN_SIZE = 0.5;

export const BAR_CHART_TICK_TOLERANCE = 0.5;

export const getBarChartLayout = ({
    valueLabelWidth,
    labelBehaviour,
    stackBehaviour,
    valueFormatter,
    orientation,
    categories,
    measures,
    weights,
    height,
    values,
    isRTL,
    scale,
    width
}: BarChartLayoutConstantType): BarChartLayout => {
    const isHorizontal = orientation === "horizontal";

    const isStacked = stackBehaviour !== "grouped";

    const isLabeled = labelBehaviour !== "none" && stackBehaviour !== "expanded";

    const {
        typography,
        bandRatio,
        spacingMd,
        spacingSm,
        spacingXs,
        barMax,
        gap
    } = measures;

    const {
        lineHeight,
        fontSize
    } = typography;

    const seriesCount = weights.length;

    const categoryWidth = Math.max(0, ...categories.map((text) => getChartTextWidth({
        fontSize,
        text
    })));

    const yAxisWidth = isHorizontal ? categoryWidth + spacingMd : Math.max(0, ...scale.labels.map((text) => getChartTextWidth({
        fontSize,
        text
    }))) + spacingSm;

    const tipReserve = isLabeled ? valueLabelWidth + spacingSm : 0;

    const plot = isHorizontal ? {
        width: Math.max(0, width - yAxisWidth - tipReserve),
        x: isRTL ? tipReserve : yAxisWidth,
        height,
        y: 0
    } : getChartPlotRect({
        inset: isLabeled ? lineHeight + spacingXs : spacingSm,
        xAxisHeight: lineHeight + spacingSm,
        yAxisWidth,
        height,
        isRTL,
        width
    });

    const toValue = getChartLinearScale({
        range: isHorizontal ? isRTL ? [
            plot.x + plot.width,
            plot.x
        ] : [
            plot.x,
            plot.x + plot.width
        ] : [
            plot.y + plot.height,
            plot.y
        ],
        domain: [
            scale.min,
            scale.max
        ]
    });

    const baseline = toValue(0);

    const bands = categories.map((_, index) => getChartBand({
        size: isHorizontal ? plot.height : plot.width,
        start: isHorizontal ? plot.y : plot.x,
        isRTL: isHorizontal ? false : isRTL,
        count: categories.length,
        index
    }));

    const bars: Array<BarChartBar> = [];

    const getSide = (value: number): BarChartBarSide => {
        if(isHorizontal) {
            return (value >= 0) !== isRTL ? "end" : "start";
        }

        return value >= 0 ? "top" : "bottom";
    };

    const pushBar = ({
        categoryIndex,
        seriesIndex,
        crossStart,
        valueStart,
        crossSize,
        valueEnd,
        value
    }: {
        categoryIndex: number;
        seriesIndex: number;
        crossStart: number;
        valueStart: number;
        crossSize: number;
        valueEnd: number;
        value: number;
    }): BarChartBar | undefined => {
        const start = Math.min(valueStart, valueEnd);

        const size = Math.abs(valueEnd - valueStart);

        if(size < BAR_CHART_MIN_SIZE || crossSize < BAR_CHART_MIN_SIZE) {
            return undefined;
        }

        const bar: BarChartBar = {
            ...(isHorizontal ? {
                height: crossSize,
                y: crossStart,
                width: size,
                x: start
            } : {
                width: crossSize,
                x: crossStart,
                height: size,
                y: start
            }),
            side: getSide(value),
            categoryIndex,
            seriesIndex,
            value
        };

        bars.push(bar);

        return bar;
    };

    values.forEach((items, categoryIndex) => {
        const band = bands[categoryIndex];

        if(!band) {
            return;
        }

        if(isStacked) {
            const crossSize = Math.min(barMax, band.size * bandRatio);

            const segments: Array<BarChartBar> = [];

            let positive = 0;

            let negative = 0;

            items.forEach((value, seriesIndex) => {
                if(!value) {
                    return;
                }

                const from = value > 0 ? positive : negative;

                const to = from + value;

                if(value > 0) {
                    positive = to;
                } else {
                    negative = to;
                }

                const rawStart = toValue(from);

                const valueEnd = toValue(to);

                const direction = Math.sign(valueEnd - rawStart);

                const valueStart = from === 0 ? rawStart : rawStart + direction * Math.min(gap, Math.abs(valueEnd - rawStart));

                const bar = pushBar({
                    crossStart: band.center - crossSize / 2,
                    categoryIndex,
                    seriesIndex,
                    valueStart,
                    crossSize,
                    valueEnd,
                    value
                });

                if(bar) {
                    bar.side = "none";

                    segments.push(bar);
                }
            });

            [
                (bar: BarChartBar) => bar.value > 0,
                (bar: BarChartBar) => bar.value < 0
            ].forEach((isSign) => {
                const outer = segments.filter(isSign).pop();

                if(outer) {
                    outer.side = getSide(outer.value);
                }
            });

            return;
        }

        const total = weights.reduce((sum, weight) => sum + weight, 0);

        const groupSize = Math.min(seriesCount * barMax + (seriesCount - 1) * gap, band.size * bandRatio);

        const crossUnit = Math.min(barMax, (groupSize - gap * Math.max(0, total - 1)) / Math.max(total, BAR_CHART_MIN_SIZE / barMax));

        const order = items.map((_, index) => index);

        if(!isHorizontal && isRTL) {
            order.reverse();
        }

        const gaps = order.map((seriesIndex, position) => {
            const previous = order[position - 1];

            return previous === undefined ? 0 : gap * Math.min(weights[previous] ?? 0, weights[seriesIndex] ?? 0);
        });

        const span = order.reduce((sum, seriesIndex, position) => sum + crossUnit * (weights[seriesIndex] ?? 0) + (gaps[position] ?? 0), 0);

        let cursor = band.center - span / 2;

        order.forEach((seriesIndex, position) => {
            const crossSize = crossUnit * (weights[seriesIndex] ?? 0);

            const value = items[seriesIndex] ?? 0;

            cursor += gaps[position] ?? 0;

            pushBar({
                valueEnd: toValue(value),
                valueStart: baseline,
                crossStart: cursor,
                categoryIndex,
                seriesIndex,
                crossSize,
                value
            });

            cursor += crossSize;
        });
    });

    const labels: Array<BarChartLabel> = [];

    if(isLabeled) {
        const totals = values.map((items) => items.reduce((sum, item) => sum + item, 0));

        const primaryValues = isStacked ? totals : values.map((items) => Math.max(...items, 0));

        getChartLabelIndexes({
            behaviour: labelBehaviour,
            values: primaryValues
        }).forEach((categoryIndex) => {
            const categoryBars = bars.filter((bar) => bar.categoryIndex === categoryIndex);

            const labelBars = isStacked ? [
                categoryBars[categoryBars.length - 1]
            ] : categoryBars;

            labelBars.forEach((bar) => {
                if(!bar) {
                    return;
                }

                const value = isStacked ? totals[categoryIndex] ?? 0 : bar.value;

                const text = valueFormatter(value);

                if(isHorizontal) {
                    const isEndSide = isStacked ? (value >= 0) !== isRTL : bar.side === "end";

                    const edges = categoryBars.map((item) => isEndSide ? item.x + item.width : item.x);

                    const edge = isStacked ? isEndSide ? Math.max(...edges) : Math.min(...edges) : isEndSide ? bar.x + bar.width : bar.x;

                    labels.push({
                        y: bar.y + bar.height / 2 + fontSize * BAR_CHART_LABEL_BASELINE_SHIFT,
                        x: isEndSide ? edge + spacingSm : edge - spacingSm,
                        anchor: isEndSide ? "start" : "end",
                        categoryIndex,
                        text
                    });

                    return;
                }

                const isPositive = value >= 0;

                const top = isStacked ? Math.min(...categoryBars.map((item) => item.y)) : bar.y;

                const bottom = isStacked ? Math.max(...categoryBars.map((item) => item.y + item.height)) : bar.y + bar.height;

                labels.push({
                    y: isPositive ? top - spacingXs : bottom + fontSize + spacingXs,
                    x: bar.x + bar.width / 2,
                    anchor: "middle",
                    categoryIndex,
                    text
                });
            });
        });
    }

    const isTickVisible = (position: number): boolean => {
        const low = isHorizontal ? plot.x : plot.y;

        const high = isHorizontal ? plot.x + plot.width : plot.y + plot.height;

        return position >= low - BAR_CHART_TICK_TOLERANCE && position <= high + BAR_CHART_TICK_TOLERANCE;
    };

    const hasNegative = scale.min < 0;

    const visibleTicks = scale.ticks.map((tick, index) => ({
        label: scale.labels[index] ?? "",
        position: toValue(tick),
        value: tick
    })).filter((tick) => isTickVisible(tick.position));

    const bandWidth = bands[0]?.size ?? 0;

    const categoryTickIndexes = isHorizontal ? [] : getChartXTicks({
        labelWidth: categoryWidth,
        count: categories.length,
        minGap: spacingSm,
        width: plot.width,
        scale: "band"
    });

    return {
        gridPositions: isHorizontal ? [] : visibleTicks.filter((tick) => tick.value !== 0 || !hasNegative).map((tick) => tick.position),
        categoryLabels: isHorizontal ? categories.map((text, index) => ({
            y: (bands[index]?.center ?? 0) + fontSize * BAR_CHART_LABEL_BASELINE_SHIFT,
            anchor: isRTL ? "end" : "start",
            categoryIndex: index,
            x: isRTL ? width : 0,
            text
        })) : [],
        categoryTicks: categoryTickIndexes.map((index) => ({
            position: bands[index]?.center ?? 0,
            label: categories[index] ?? ""
        })).filter(() => bandWidth > 0),
        valueTicks: isHorizontal ? [] : visibleTicks.map(({
            position,
            label
        }) => ({
            position,
            label
        })),
        baseline,
        labels,
        bands,
        plot,
        bars
    };
};
